import { useState } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { tokens } from '../theme';

/** Shown to every route when the visitor's network is not on the IP allowlist. */
export function AccessDeniedPage({ ip }: { ip: string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(ip);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard unavailable — the address is still visible */
    }
  }

  return (
    <Box minHeight="100vh" display="flex" flexDirection="column" bgcolor="#fff">
      <Box sx={{ bgcolor: tokens.strip, borderBottom: `1px solid ${tokens.divider}`, px: { xs: 2, sm: 4 }, py: 1 }}>
        <Typography fontSize={13} color="#344054" sx={{ maxWidth: 1240, mx: 'auto' }}>
          An Advanced Substrate Technologies internal site
        </Typography>
      </Box>
      <Box component="header" sx={{ borderBottom: `1px solid ${tokens.divider}` }}>
        <Stack direction="row" alignItems="center" spacing={1.75} sx={{ maxWidth: 1240, mx: 'auto', px: { xs: 2, sm: 4 }, py: 1.75 }}>
          <Box component="img" src="/ast-logo.png" alt="AST" sx={{ height: 32, width: 'auto' }} />
          <Typography fontWeight={700} fontSize={17} color={tokens.ink}>
            IT Lab
          </Typography>
        </Stack>
      </Box>

      <Box component="main" flex={1} sx={{ bgcolor: tokens.subtle }}>
        <Stack spacing={4} sx={{ maxWidth: 720, mx: 'auto', px: { xs: 2, sm: 4 }, py: { xs: 7, md: 12 } }}>
          <Box
            aria-hidden
            sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: tokens.errorSoft, color: tokens.error, display: 'grid', placeItems: 'center' }}
          >
            <BlockIcon />
          </Box>
          <Stack spacing={2}>
            <Typography variant="h1" sx={{ fontSize: { xs: 36, md: 48 } }}>
              Access denied
            </Typography>
            <Typography sx={{ fontSize: { xs: 18, md: 20 }, color: 'text.secondary' }}>
              The AST IT Lab portal is only available from approved AST networks. Your current network isn’t on
              the list.
            </Typography>
          </Stack>

          <Box sx={{ p: 3, bgcolor: '#fff', border: `1px solid ${tokens.border}`, borderRadius: 2 }}>
            <Typography fontSize={14} color="text.secondary">
              Your IP address
            </Typography>
            <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap" mt={0.5}>
              <Typography
                component="code"
                sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 22, fontWeight: 600, color: tokens.ink }}
              >
                {ip || 'Unknown'}
              </Typography>
              {ip && (
                <Button size="small" variant="outlined" startIcon={<ContentCopyIcon fontSize="small" />} onClick={copy}>
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              )}
            </Stack>
          </Box>

          <Stack spacing={1.5}>
            <Typography variant="h2" fontSize={22}>
              What you can do
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 3, color: '#344054', '& li': { mb: 1 } }}>
              <li>Connect from the AST office network or the company VPN, then try again.</li>
              <li>If you should have access from here, send the IP address above to an IT Lab admin and ask them to add it.</li>
            </Box>
          </Stack>

          <Box>
            <Button variant="contained" size="large" onClick={() => window.location.reload()}>
              Try again
            </Button>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}
